import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { RefreshCw, Plus } from 'lucide-react';
import { SectionHeader } from '../../components/ui';
import { UsageDashboard, SubscriptionCard, AvailableFeaturesCard, PlanComparison, SubscriptionModal } from '../../components/subscription';
import { Subscription } from '../../types/subscription';
import { SUBSCRIPTION_PLAN_IDS } from '../../types/subscription';
import { subscriptionService } from '../../services/subscriptionService';
import { subscriptionStatusService } from '../../services/subscriptionStatusService';
import TrialExpiredModal from '../../components/subscription/TrialExpiredModal';
import AdFreePurchaseCard from '../../components/subscription/AdFreePurchaseCard';

const Subscriptions: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, isRTL, strings } = useLocalization();
  const { user } = useAuth();
  const { loadSubscription } = useSubscription();
  
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPlanComparison, setShowPlanComparison] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState<string | null>(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [userSkippedTrial, setUserSkippedTrial] = useState<boolean>(false);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);

  // Handle upgrade message from navigation
  useEffect(() => {
    if (location.state?.upgradeMessage) {
      setUpgradeMessage(location.state.upgradeMessage);
      setShowPlanComparison(true);
    }
  }, [location.state]);

  // Load subscription data
  useEffect(() => {
    const loadSubscriptionData = async () => {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Load subscription
        console.log('Loading subscription for user:', user.id);
        const userSubscription = await subscriptionService.getUserSubscription(user.id);
        console.log('Loaded subscription:', userSubscription);
        setSubscription(userSubscription);

        // Check for trial expiration (only if user is still on professional plan and in trial)
        if (userSubscription && 
            userSubscription.planId === SUBSCRIPTION_PLAN_IDS.PROFESSIONAL && 
            userSubscription.status === 'trial') {
          console.log('🔍 Checking trial expiration for subscription:', userSubscription);
          const result = await subscriptionStatusService.checkAndUpdateSubscriptionStatus(user.id, userSubscription);
          console.log('🔍 Trial expiration check result:', result);
          
          if (result.showTrialExpiredModal) {
            console.log('🔔 Trial expired - showing modal');
            setShowTrialExpiredModal(true);
          }
          
          if (result.wasUpdated && result.updatedSubscription) {
            console.log('✅ Subscription was updated:', result.updatedSubscription);
            setSubscription(result.updatedSubscription);
          }
        } else {
          console.log('🔍 Skipping trial expiration check - user not on professional trial plan');
          console.log('📊 Current subscription:', {
            planId: userSubscription?.planId,
            status: userSubscription?.status
          });
        }
        
        // Load user data to check if they skipped trial offer
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('../../config/firebase');
          const userDoc = await getDoc(doc(db, 'Users', user.id));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserSkippedTrial(userData.skippedTrialOffer || false);
          }
        } catch (err) {
          console.error('Error loading user trial status:', err);
          setUserSkippedTrial(false);
        }
        
        // Load usage stats
        console.log('Loading usage stats for user:', user.id);
        // Note: Usage stats will be calculated locally from cache
        console.log('Usage stats will be loaded from cache context');
        
        // If no subscription found, this might be a new user - we'll show the plan comparison
        if (!userSubscription) {
          console.log('No subscription found for user, showing plan selection');
          setShowPlanComparison(true);
        }
      } catch (err) {
        console.error('Error loading subscription data:', err);
        setError('Failed to load subscription data');
      } finally {
        setLoading(false);
      }
    };

    loadSubscriptionData();
  }, [user?.id]);

  const handleRefresh = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const userSubscription = await subscriptionService.getUserSubscription(user.id);
      setSubscription(userSubscription);
      
      // Note: Usage stats will be calculated locally from cache
    } catch (err) {
      console.error('Error refreshing subscription data:', err);
      setError('Failed to refresh subscription data');
    } finally {
      setLoading(false);
    }
  };


  const handleShowPlans = () => {
    setShowPlanComparison(true);
  };

  const handleUpgrade = (planId: string) => {
    console.log('Upgrade to plan:', planId);
    // TODO: Implement upgrade functionality
  };

  const handleSubscribe = (planId: string) => {
    setSelectedPlanId(planId);
    setShowSubscriptionModal(true);
  };

  const handleSubscriptionSuccess = async (newSubscription: Subscription) => {
    console.log('Subscription created successfully:', newSubscription);
    setSubscription(newSubscription);
    setShowSubscriptionModal(false);
    setShowPlanComparison(false);
    
    // Refresh subscription data
    await handleRefresh();
    
    // Also refresh subscription context to update plan limitations
    if (user?.id) {
      console.log('🔄 Refreshing subscription context after successful subscription');
      await loadSubscription(user.id);
    }
  };

  const handleSubscriptionError = (error: string) => {
    console.error('Subscription error:', error);
    setError(error);
  };

  const handleHidePlans = () => {
    setShowPlanComparison(false);
  };

  // Trial expired modal handlers
  const handleTrialExpiredSubscribe = () => {
    setShowTrialExpiredModal(false);
    // Navigate to subscription page
    navigate('/dashboard/subscriptions');
  };

  const handleTrialExpiredContinueFree = async () => {
    if (user?.id && subscription) {
      try {
        console.log('🔧 User chose to continue with free plan - downgrading subscription');
        const updatedSubscription = await subscriptionStatusService.handleTrialExpiration(user.id, subscription);
        console.log('✅ Subscription downgraded to free plan');
        setSubscription(updatedSubscription);
        
        // Refresh subscription context to update plan limitations
        console.log('🔄 Refreshing subscription context after trial expiration');
        await loadSubscription(user.id);
      } catch (error) {
        console.error('❌ Error downgrading subscription:', error);
      }
    }
    setShowTrialExpiredModal(false);
    // Stay on subscription page to show the updated subscription
    console.log('✅ Staying on subscription page to show updated subscription');
  };

  const handleTrialExpiredClose = async () => {
    // User closed modal - treat as continuing with free plan
    await handleTrialExpiredContinueFree();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
            <SectionHeader
              title={strings.subscriptions.title}
              description={strings.subscriptions.subtitle}
          language={language}
          isRTL={isRTL}
          refreshButton={{
            onClick: handleRefresh,
            disabled: loading,
            loading: loading
          }}
          addButton={{
            label: strings.subscriptions.viewPlans,
            onClick: handleShowPlans
          }}
        />

        {/* Trial Status Indicator */}
        {subscription && subscription.status === 'trial' && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="text-3xl">🎉</div>
                <div>
                  <h3 className="text-lg font-semibold text-blue-900">
                    {language === 'ar' ? 'أنت في فترة تجريبية مجانية!' : 'You\'re in a Free Trial!'}
                  </h3>
                  <p className="text-blue-700 text-sm">
                    {language === 'ar' 
                      ? `متبقي ${subscription.endDate ? Math.ceil((parseInt(subscription.endDate) - Date.now()) / (1000 * 60 * 60 * 24)) : 14} أيام من الفترة التجريبية`
                      : `${subscription.endDate ? Math.ceil((parseInt(subscription.endDate) - Date.now()) / (1000 * 60 * 60 * 24)) : 14} days left in your trial`
                    }
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-600">
                  {language === 'ar' ? 'استمتع بجميع المميزات' : 'Enjoy all features'}
                </p>
                <p className="text-xs text-blue-500">
                  {language === 'ar' 
                    ? `بعد انتهاء الفترة التجريبية: ${subscription.totalPrice} ${subscription.currency}/شهر`
                    : `After trial: ${subscription.totalPrice} ${subscription.currency}/month`
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-gray-600">
              {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
            </span>
          </div>
        )}

        {/* Upgrade Message */}
        {upgradeMessage && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-lg">👑</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-800">
                  {language === 'ar' ? 'ميزة متقدمة' : 'Premium Feature'}
                </h3>
                <p className="text-sm text-blue-700 mt-1">{upgradeMessage}</p>
                <button
                  onClick={() => setShowPlanComparison(true)}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  {language === 'ar' ? 'عرض الخطط المتاحة' : 'View Available Plans'}
                </button>
              </div>
              <button
                onClick={() => setUpgradeMessage(null)}
                className="flex-shrink-0 text-blue-400 hover:text-blue-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              {language === 'ar' ? 'إعادة المحاولة' : 'Try Again'}
            </button>
          </div>
        )}

        {/* OW-174 / OW-176 / OW-177 — Ad-Free purchase + status */}
        {user?.id && (
          <div className="mb-6">
            <AdFreePurchaseCard language={language} isRTL={isRTL} />
          </div>
        )}

        {/* Main Content */}
        {!loading && !error && (
          <div className="space-y-6">
            {/* Current Subscription and Usage */}
            {subscription ? (
              <div className="space-y-6">
                {/* Main Cards Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Subscription Card */}
                  <SubscriptionCard
                    subscription={subscription}
                  />

                  {/* Usage Dashboard */}
                  <UsageDashboard
                    subscription={subscription}
                  />
                </div>

                {/* Available Features Card */}
                <AvailableFeaturesCard
                  subscription={subscription}
                />
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                <div className="text-blue-600 text-4xl mb-4">📋</div>
                <h3 className="text-lg font-semibold text-blue-800 mb-2">
                  {language === 'ar' ? 'لا يوجد اشتراك نشط' : 'No Active Subscription'}
                </h3>
                <p className="text-blue-600 mb-4">
                  {language === 'ar' 
                    ? 'اختر خطة اشتراك لبدء استخدام جميع المميزات'
                    : 'Choose a subscription plan to start using all features'
                  }
                </p>
                <button
                  onClick={handleShowPlans}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  {language === 'ar' ? 'عرض الخطط' : 'View Plans'}
                </button>
              </div>
            )}


            {/* Plan Comparison */}
            {showPlanComparison && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {language === 'ar' ? 'مقارنة الخطط' : 'Plan Comparison'}
                  </h2>
                  <button
                    onClick={handleHidePlans}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    {language === 'ar' ? 'إغلاق' : 'Close'}
                  </button>
                </div>
                <PlanComparison
                  currentPlanId={subscription?.planId}
                  onUpgrade={handleUpgrade}
                  onSubscribe={handleSubscribe}
                />
              </div>
            )}

          </div>
        )}

        {/* Subscription Modal */}
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          selectedPlanId={selectedPlanId}
          onSuccess={handleSubscriptionSuccess}
          onError={handleSubscriptionError}
          showTrialOption={userSkippedTrial}
        />

        {/* Trial Expired Modal */}
        <TrialExpiredModal
          isOpen={showTrialExpiredModal}
          onClose={handleTrialExpiredClose}
          onSubscribe={handleTrialExpiredSubscribe}
          onContinueFree={handleTrialExpiredContinueFree}
        />
      </div>
    </div>
  );
};

export default Subscriptions;
